import { useCallback, useEffect, useRef, useState } from "react";

// What the athlete's first edit of an untouched seed means. The field holds
// `seed`; the platform reports `next` after that edit. Whatever was typed or
// pasted replaces the whole seed; a pure deletion (Backspace) clears it.
export function resolveFirstEdit(seed, next) {
  if (next === seed) return seed;
  const limit = Math.min(seed.length, next.length);
  let prefix = 0;
  while (prefix < limit && seed[prefix] === next[prefix]) prefix += 1;
  let suffix = 0;
  while (
    suffix < limit - prefix &&
    seed[seed.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  return next.slice(prefix, next.length - suffix);
}

// LAST-assisted field entry (§13.10). Directly tapping or clicking an empty
// field seeds its draft with that field's usable LAST value. The seed is a
// real draft value: leaving the field commits it through the field's normal
// path. While it is untouched, the first edit replaces it entirely; that is
// tracked as explicit state, never through a native select-all, so no text
// selection (or iOS selection handles) appears. Nothing is seeded by receiving
// history, by keyboard traversal or by programmatic focus.
export default function useLastSeed({ seedValue, isEmpty, disabled, setDraft }) {
  const inputRef = useRef(null);
  const [seeded, setSeeded] = useState(null);

  // A plain caret at the end of the seeded value, with nothing selected.
  useEffect(() => {
    if (seeded == null) return;
    try {
      inputRef.current?.setSelectionRange?.(seeded.length, seeded.length);
    } catch (_error) {
      // Caret placement is cosmetic; replacement is enforced by resolveChange.
    }
  }, [seeded]);

  const activate = useCallback(() => {
    if (disabled || !isEmpty || seedValue == null || seeded != null) return;
    const next = String(seedValue);
    setDraft(next);
    setSeeded(next);
  }, [disabled, isEmpty, seedValue, seeded, setDraft]);

  // The value the field should take for a change event. After the first edit
  // the field is back to normal editing.
  const resolveChange = useCallback((nextValue) => {
    if (seeded == null) return nextValue;
    const resolved = resolveFirstEdit(seeded, nextValue);
    if (resolved !== seeded) setSeeded(null);
    return resolved;
  }, [seeded]);

  const release = useCallback(() => setSeeded(null), []);

  return { inputRef, activate, resolveChange, release, seeded: seeded != null };
}
